import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'

import { DevicesApi, type DeviceDimension } from '@/services/targeting/devicesApi'
import { BadgeToggleGroup } from '../BadgeToggleGroup'

export interface DeviceTargetRow {
  targetedDeviceTypes?: string[]
  targetedOSes?: string[]
  targetedBrands?: string[]
}

/** The dimensions this editor picks; brands are left to the API. */
const EDITED_DIMENSIONS: DeviceDimension[] = ['targetedDeviceTypes', 'targetedOSes']

export interface DeviceTargetEditorProps {
  value: DeviceTargetRow[]
  onChange: (value: DeviceTargetRow[]) => void
}

/**
 * Device targeting: the device types and operating systems to deliver on,
 * picked as badges. The two dimensions must both match, and within one
 * dimension the picked values are alternatives.
 *
 * The API allows several rows of alternative combinations, and a row can also
 * name device brands. Neither can be expressed here, so only the first row's
 * two dimensions are edited and everything else is carried through untouched.
 */
export function DeviceTargetEditor({ value, onChange }: DeviceTargetEditorProps) {
  const { t } = useTranslation()

  const { data: properties, isLoading } = useQuery({
    queryKey: ['device-properties'],
    queryFn: DevicesApi.getAll,
    staleTime: 60 * 60 * 1000,
  })

  const row = value[0] ?? {}
  const carried = (row.targetedBrands?.length ?? 0) > 0 || value.length > 1

  const update = (dimension: DeviceDimension, selected: string[]) => {
    const first = { ...row, [dimension]: selected }
    // A row that targets nothing is no row at all.
    const empty = EDITED_DIMENSIONS.every((d) => !first[d]?.length) && !first.targetedBrands?.length
    const rest = value.slice(1)
    onChange(empty ? rest : [first, ...rest])
  }

  return (
    <div className="flex flex-col gap-5">
      {EDITED_DIMENSIONS.map((dimension) => (
        <BadgeToggleGroup
          key={dimension}
          label={t(`targeting.device.${dimension}`)}
          options={(properties?.[dimension] ?? []).map((option) => ({
            label: t(`targeting.device.values.${option}`, option.replace(/_/g, ' ')),
            value: option,
          }))}
          value={row[dimension] ?? []}
          onChange={(selected) => update(dimension, selected)}
          loading={isLoading}
        />
      ))}

      {EDITED_DIMENSIONS.every((dimension) => !row[dimension]?.length) && !carried && (
        <p className="text-xs text-muted-foreground">{t('targeting.device.empty')}</p>
      )}
      {carried && <p className="text-xs text-muted-foreground">{t('targeting.device.carried')}</p>}
    </div>
  )
}
